import React, { useId, useState, useCallback, useEffect, useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import classnames from "classnames";
import * as Form from "@radix-ui/react-form";
import { useDispatch, useSelector } from "react-redux";

import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";
import { RichText } from "@learningblocks/shared-components/lib/RichText";

import { LxButton } from "../LxButton";
import { Collapsible } from "../LxCollapsible";
import { Figure } from "../Figure";
import { FeedbackBanner } from "../FeedbackBanner";
import {
  updateTableCell,
  updateTableState,
  initializeTable,
  submitTableAnswers,
  selectTableAttemptsLeft,
  selectTableModifiedCells,
  selectTableValues,
  type TableQuestionState,
} from "./TableQuestionSlice";

import { fetchTranslations } from "./locales/config";
import {
  TableQuestionProps,
  TableCellData,
  CellChangeParams,
  ButtonClickParams,
  AnswerType,
} from "./types";
import { renderTableCell } from "./cellRenderers";
import {
  tableQuestionClass,
  tableReadonlyClass,
  tableQuestionTransparentClass,
  containerClass,
  containerReadonlyClass,
  containerTransparentClass,
  questionCategoryClass,
  questionHeaderClass,
  questionTitleClass,
  questionTextClass,
  questionTextParagraphClass,
  tableWrapperClass,
  tableClass,
  tableRowClass,
  footerClass,
  attemptsTextClass,
  hintAreaClass,
  hintTextClass,
  collapsibleClass,
  feedbackMessagesContainerClass,
} from "./TableQuestion.css";

/* -------------------------------- Messages -------------------------------- */

export const messages = defineMessages({
  tableQuestionHeader: {
    id: "TableQuestion.header",
    defaultMessage: "Table Question",
    description: "Header text for table question type.",
  },
  predictionHeader: {
    id: "TableQuestion.predictionHeader",
    defaultMessage: "Prediction Table",
    description: "Header text for prediction table type.",
  },
  attemptsLeft: {
    id: "TableQuestion.attemptsLeft",
    defaultMessage: "Attempts left: {attemptsLeft}",
    description: "Label showing the number of attempts left.",
  },
  submitAnswer: {
    id: "TableQuestion.submitAnswer",
    defaultMessage: "Submit answer",
    description: "Text for the submit button.",
  },
  submitPrediction: {
    id: "TableQuestion.submitPrediction",
    defaultMessage: "Submit prediction",
    description: "Text for the submit prediction button.",
  },
  correctFeedbackDefault: {
    id: "TableQuestion.correctFeedbackDefault",
    defaultMessage: "Correct!",
    description: "Default feedback for correct answer.",
  },
  incorrectFeedbackDefault: {
    id: "TableQuestion.incorrectFeedbackDefault",
    defaultMessage:
      "Not quite. {incorrectCount}/{totalCount} answers are incorrect. Try again.",
    description: "Default feedback for incorrect answer.",
  },
  predictionSubmitted: {
    id: "TableQuestion.predictionSubmitted",
    defaultMessage:
      "Prediction submitted. You will compare this answer with your final result.",
    description: "Text shown when prediction is submitted.",
  },
  showHint: {
    id: "TableQuestion.showHint",
    defaultMessage: "Show hint",
    description: "Text for the show hint button.",
  },
  nextHint: {
    id: "TableQuestion.nextHint",
    defaultMessage: "Next hint",
    description: "Text for the next hint button.",
  },
  resetPrediction: {
    id: "TableQuestion.resetPrediction",
    defaultMessage: "Reset prediction",
    description: "Text for the reset prediction button.",
  },
});

/* -------------------------------- Helper Functions -------------------------------- */

const containsMathJax = (text: string | undefined): boolean => {
  if (!text || typeof text !== "string") return false;
  return /\$\$.*?\$\$|\$.*?\$/g.test(text);
};

const hasEmptyEditableCells = (data: TableCellData[][]): boolean => {
  for (const row of data) {
    for (const cell of row) {
      if (cell.type === "INPUT" || cell.type === "DROPDOWN") {
        if (
          cell.value === null ||
          cell.value === undefined ||
          cell.value === ""
        ) {
          return true;
        }
      }
    }
  }
  return false;
};

const hasInteractiveCells = (data: TableCellData[][]): boolean => {
  for (const row of data) {
    for (const cell of row) {
      if (
        cell.type === "INPUT" ||
        cell.type === "DROPDOWN" ||
        cell.type === "BUTTON"
      ) {
        return true;
      }
    }
  }
  return false;
};

const validateCellAnswer = (
  value: string | number | null | undefined,
  answer: AnswerType | undefined,
  accuracy?: number
): boolean => {
  if (value === null || value === undefined || value === "") return false;
  if (answer === undefined) return true;

  if (typeof answer === "string") {
    return answer.toLowerCase() === String(value).toLowerCase();
  }

  if (typeof answer === "number") {
    const numValue = Number(value);
    if (accuracy !== undefined) {
      return Math.abs(numValue - answer) < Math.pow(10, -accuracy);
    }
    return numValue === answer;
  }

  if (typeof answer === "object" && answer !== null && !Array.isArray(answer)) {
    const numValue = Number(value);
    const { min, max } = answer;
    if (min !== undefined && numValue < min) return false;
    if (max !== undefined && numValue > max) return false;
    return true;
  }

  if (Array.isArray(answer)) {
    return answer.some((a) => {
      if (typeof a === "string") {
        return a.toLowerCase() === String(value).toLowerCase();
      }
      return a === Number(value);
    });
  }

  return false;
};

const countAnswerableCells = (
  data: TableCellData[][]
): { totalCount: number; incorrectCount: number } => {
  let totalCount = 0;
  let incorrectCount = 0;

  for (const row of data) {
    for (const cell of row) {
      if (cell.answer !== undefined) {
        totalCount++;
        const isCorrect = validateCellAnswer(
          cell.value,
          cell.answer,
          cell.accuracy
        );
        if (!isCorrect) {
          incorrectCount++;
        }
      }
    }
  }

  return { totalCount, incorrectCount };
};

/* -------------------------------- Sub Components -------------------------------- */

interface QuestionHeaderProps {
  id: string;
  isPrediction: boolean;
  heading?: string;
}

const QuestionHeader: React.FC<QuestionHeaderProps> = ({
  id,
  isPrediction,
  heading,
}) => {
  const { formatMessage } = useIntl();

  const headerText =
    heading ||
    formatMessage(
      isPrediction ? messages.predictionHeader : messages.tableQuestionHeader
    );

  return (
    <p id={id} className={questionCategoryClass}>
      {headerText}
    </p>
  );
};

/* -------------------------------- Main Component -------------------------------- */

const TableQuestionInternal: React.FC<TableQuestionProps> = ({
  id,
  type,
  data,
  content = ["title", "text", "table"],
  title,
  text,
  question,
  heading,
  image,
  questionType,
  totalAttempts = -1,
  isSubmitted: isSubmittedProp = false,
  submitDisabled,
  buttonText,
  hideFooter,
  hideAttemptsLeft,
  showQuestionHeading = false,
  hintDetails,
  hints,
  hintIndex = -1,
  showHint,
  correctFeedback,
  incorrectFeedback,
  correct,
  feedbackMessages,
  onCellChange,
  onButtonClick,
  submitAnswer,
  setShowHint,
  showNextHint,
  setCompleted,
  onReset,
  className,
  striped,
  isTransparent = false,
  showFeedback = true,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const componentId = useId();
  const headerId = `${componentId}-header`;

  // Get redux state from Redux
  const reduxAttemptsLeft = useSelector(
    (state: { tableQuestion: TableQuestionState }) =>
      selectTableAttemptsLeft(state, id)
  );
  const reduxModifiedCells = useSelector(
    (state: { tableQuestion: TableQuestionState }) =>
      selectTableModifiedCells(state, id)
  );
  const reduxCellValues = useSelector(
    (state: { tableQuestion: TableQuestionState }) =>
      selectTableValues(state, id)
  );

  // Initialize state from Redux if available, otherwise use default values
  const [attemptsLeft, setAttemptsLeft] = useState<number>(
    reduxAttemptsLeft ??
      (typeof totalAttempts === "string"
        ? parseInt(totalAttempts, 10)
        : totalAttempts)
  );
  // Use component's isSubmittedProp as the source of truth, not Redux
  // This prevents shared Redux state from affecting other table instances
  const [isSubmitted, setIsSubmitted] = useState(isSubmittedProp);
  const [modifiedCells, setModifiedCells] = useState<Set<string>>(
    new Set(reduxModifiedCells ?? [])
  );

  // Initialize table in Redux on mount
  useEffect(() => {
    if (reduxAttemptsLeft === undefined) {
      dispatch(
        initializeTable({
          tableId: id,
          attemptsLeft:
            typeof totalAttempts === "string"
              ? parseInt(totalAttempts, 10)
              : totalAttempts,
        })
      );
    }
  }, [dispatch, id, reduxAttemptsLeft, totalAttempts]);

  // Merge redux cell values into data
  const dataWithReduxValues = useMemo(() => {
    if (!reduxCellValues) return data;

    return data.map((row, rowIndex) =>
      row.map((cell, colIndex) => {
        const cellKey = `${rowIndex}_${colIndex}`;
        const reduxValue = reduxCellValues[cellKey];

        if (
          reduxValue !== undefined &&
          (cell.type === "INPUT" || cell.type === "DROPDOWN")
        ) {
          const isDefaultValue =
            cell.value === undefined ||
            cell.value === null ||
            cell.value === "";

          if (isDefaultValue) {
            return { ...cell, value: reduxValue };
          }
        }

        return cell;
      })
    );
  }, [data, reduxCellValues]);

  const isPrediction = questionType === "prediction";
  const isDataTable = type === "DATA_TABLE";
  const isReadOnly = isDataTable && !hasInteractiveCells(dataWithReduxValues);

  const checkAllAnswersCorrect = useCallback((): boolean => {
    let allCorrect = true;
    const uniqueValues: (string | number)[] = [];

    dataWithReduxValues.forEach((row) => {
      row.forEach((cell) => {
        if (cell.type === "INPUT" || cell.type === "DROPDOWN") {
          const isValid = validateCellAnswer(
            cell.value,
            cell.answer,
            cell.accuracy
          );

          if (!isValid) {
            allCorrect = false;
          }

          if (cell.unique && cell.value !== null && cell.value !== undefined) {
            if (uniqueValues.includes(cell.value)) {
              allCorrect = false;
            }
            uniqueValues.push(cell.value);
          }
        }
      });
    });

    return allCorrect;
  }, [dataWithReduxValues]);

  const handleCellChange = useCallback(
    (params: Omit<CellChangeParams, "componentID">) => {
      const cellKey = `${params.rowId}-${params.colId}`;
      setModifiedCells((prev) => {
        const updated = new Set(prev).add(cellKey);
        dispatch(
          updateTableState({
            tableId: id,
            modifiedCells: Array.from(updated),
          })
        );
        return updated;
      });
      dispatch(
        updateTableCell({
          tableId: id,
          rowId: params.rowId,
          colId: params.colId,
          value: params.value,
        })
      );

      onCellChange?.({
        componentID: id,
        ...params,
      });
    },
    [onCellChange, id, dispatch]
  );

  const handleButtonClick = useCallback(
    (params: Omit<ButtonClickParams, "componentID">) => {
      onButtonClick?.({
        componentID: id,
        ...params,
      });
    },
    [onButtonClick, id]
  );

  const handleSubmit = useCallback(() => {
    if (showHint) {
      setShowHint?.({ componentID: id, showHint: false });
    }

    // Dispatch submission action for tracking
    dispatch(
      submitTableAnswers({
        tableId: id,
      })
    );

    let newAttemptsLeft = attemptsLeft;
    if (attemptsLeft > 0) {
      newAttemptsLeft = attemptsLeft - 1;
      setAttemptsLeft(newAttemptsLeft);
    }

    setIsSubmitted(true);
    setModifiedCells(new Set());
    submitAnswer?.(id, type);

    if (isPrediction || checkAllAnswersCorrect()) {
      setCompleted?.(true);
    }
  }, [
    showHint,
    setShowHint,
    id,
    attemptsLeft,
    submitAnswer,
    type,
    isPrediction,
    checkAllAnswersCorrect,
    setCompleted,
    dispatch,
  ]);

  const handleShowHint = useCallback(() => {
    if (!showHint) {
      setShowHint?.({ componentID: id, showHint: true });
    } else {
      showNextHint?.({ componentID: id });
    }
  }, [showHint, setShowHint, showNextHint, id]);

  const getFeedbackText = useCallback(
    (isCorrect: boolean): string => {
      const feedbackProp = isCorrect ? correctFeedback : incorrectFeedback;
      const defaultMessage = isCorrect
        ? messages.correctFeedbackDefault
        : messages.incorrectFeedbackDefault;

      if (typeof feedbackProp === "string") {
        return intl.formatMessage({
          id: feedbackProp,
          defaultMessage: feedbackProp,
          description: "Custom feedback message for table question",
        });
      }

      if (!isCorrect) {
        const { totalCount, incorrectCount } =
          countAnswerableCells(dataWithReduxValues);
        return intl.formatMessage(defaultMessage, {
          incorrectCount,
          totalCount,
        });
      }

      return intl.formatMessage(defaultMessage);
    },
    [intl, correctFeedback, incorrectFeedback, dataWithReduxValues]
  );

  const renderCell = (
    cell: TableCellData,
    rowIndex: number,
    colIndex: number
  ) => {
    const cellId = `${componentId}-cell-${rowIndex}-${colIndex}`;
    const cellKey = `${rowIndex}-${colIndex}`;

    // Check if cell has a non-default value from the data prop (set by middleware)
    // If so, ignore the modifiedCells state for this cell since it was set externally
    // Default values include: undefined, null, "", "0", 0 (placeholder values in initial data)
    const originalCell = data[rowIndex]?.[colIndex];
    const originalValue = originalCell?.value;
    const hasDataPropValue =
      originalCell &&
      (originalCell.type === "INPUT" || originalCell.type === "DROPDOWN") &&
      originalValue !== undefined &&
      originalValue !== null &&
      originalValue !== "" &&
      originalValue !== "0" &&
      originalValue !== 0;

    // Only consider a cell as "modified by user" if it doesn't have a value from data prop
    const isCellModified = hasDataPropValue
      ? false
      : modifiedCells.has(cellKey);

    const isCorrect =
      showFeedback &&
      validateCellAnswer(cell.value, cell.answer, cell.accuracy);

    return renderTableCell({
      cellId,
      cell,
      rowIndex,
      colIndex,
      isCorrect,
      isPrediction,
      isSubmitted: isSubmitted && !isCellModified,
      striped,
      onCellChange: handleCellChange,
      onButtonClick: handleButtonClick,
    });
  };

  const renderTable = () => (
    <Form.Root>
      <div className={tableWrapperClass}>
        <table className={tableClass}>
          <tbody>
            {dataWithReduxValues.map((row, rowIndex) => (
              <tr
                key={`${componentId}-row-${rowIndex}`}
                className={tableRowClass}
              >
                {row.map((cell, colIndex) =>
                  renderCell(cell, rowIndex, colIndex)
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Form.Root>
  );

  const renderHints = () => {
    if (!hints || !showHint) return null;

    const visibleHints = hints.slice(0, (hintIndex ?? 0) + 1);

    return (
      <div className={hintAreaClass}>
        {visibleHints.map((hint, index) => (
          <p key={`hint-${index}`} className={hintTextClass}>
            {`Row: ${hint.rowId}, Col: ${hint.colId}, Feedback: ${hint.feedback}`}
          </p>
        ))}
      </div>
    );
  };

  const hasMoreHints =
    hints &&
    (hints.length > (hintIndex ?? 0) + 1 ||
      (hints.length === (hintIndex ?? 0) + 1 && !showHint));

  const contentRenderers: Record<
    string,
    (index: number) => React.ReactNode | null
  > = {
    title: (index) =>
      title ? (
        <div key={`title-${index}`} className={questionTitleClass}>
          <p className={questionTextParagraphClass}>
            <RichText value={title} equation={containsMathJax(title)} />
          </p>
        </div>
      ) : null,

    text: (index) =>
      text ? (
        <div key={`text-${index}`} className={questionTextClass}>
          <p className={questionTextParagraphClass}>
            <RichText value={text} equation={containsMathJax(text)} />
          </p>
        </div>
      ) : null,

    question: (index) =>
      question ? (
        <div key={`question-${index}`} className={questionTextClass}>
          <p className={questionTextParagraphClass}>
            <RichText value={question} equation={containsMathJax(question)} />
          </p>
        </div>
      ) : null,

    image: (index) => {
      if (!image) return null;
      const imageData = typeof image === "string" ? { src: image } : image;
      return (
        <Figure
          key={`image-${index}`}
          src={imageData.src}
          altText={imageData.altText || ""}
          caption={imageData.caption}
        />
      );
    },

    table: (index) => (
      <React.Fragment key={`table-${index}`}>{renderTable()}</React.Fragment>
    ),

    hint: (index) => {
      if (!hintDetails) return null;
      const isOpen = hintDetails.open !== undefined ? hintDetails.open : false;
      return (
        <div key={`hint-${index}`} className={collapsibleClass}>
          <Collapsible
            label={hintDetails.label}
            content={hintDetails.content}
            open={isOpen}
          />
        </div>
      );
    },
  };

  const renderContent = () => {
    const elements: React.ReactNode[] = [];

    if (showQuestionHeading) {
      elements.push(
        <QuestionHeader
          key="header"
          id={headerId}
          isPrediction={isPrediction}
          heading={heading}
        />
      );
    }

    let i = 0;
    while (i < content.length) {
      const contentType = content[i];

      // Group title and text together in a header wrapper
      if (contentType === "title" && content[i + 1] === "text") {
        const titleElement = title ? (
          <div key={`title-${i}`} className={questionTitleClass}>
            <p className={questionTextParagraphClass}>
              <RichText value={title} equation={containsMathJax(title)} />
            </p>
          </div>
        ) : null;

        const textElement = text ? (
          <div key={`text-${i + 1}`} className={questionTextClass}>
            <p className={questionTextParagraphClass}>
              <RichText value={text} equation={containsMathJax(text)} />
            </p>
          </div>
        ) : null;

        if (titleElement || textElement) {
          elements.push(
            <div key={`header-group-${i}`} className={questionHeaderClass}>
              {titleElement}
              {textElement}
            </div>
          );
        }
        i += 2;
        continue;
      }

      const renderer = contentRenderers[contentType];
      if (renderer) {
        const element = renderer(i);
        if (element) {
          elements.push(element);
        }
      }
      i++;
    }

    return elements;
  };

  const submitButtonText =
    buttonText || isPrediction
      ? messages.submitPrediction.id
      : messages.submitAnswer.id;

  return (
    <div
      className={classnames(
        isReadOnly
          ? tableReadonlyClass
          : isTransparent
            ? tableQuestionTransparentClass
            : tableQuestionClass,
        className
      )}
      data-name="Table question"
    >
      <div
        className={
          isReadOnly
            ? containerReadonlyClass
            : isTransparent
              ? containerTransparentClass
              : containerClass
        }
      >
        {renderContent()}
        {renderHints()}

        {showFeedback &&
          isSubmitted &&
          !isPrediction &&
          (!feedbackMessages || feedbackMessages.length === 0) && (
            <FeedbackBanner
              variant={correct ? "success" : "fail"}
              message={getFeedbackText(correct as boolean)}
            />
          )}

        {showFeedback && isPrediction && isSubmitted && (
          <FeedbackBanner
            variant="information"
            message={messages.predictionSubmitted}
          />
        )}

        {showFeedback && feedbackMessages && feedbackMessages.length > 0 && (
          <div className={feedbackMessagesContainerClass}>
            {feedbackMessages.map((message) => (
              <FeedbackBanner
                key={message.id}
                variant={message.type ?? "success"}
                message={message.text}
              />
            ))}
          </div>
        )}

        {!hideFooter && !isDataTable && (
          <div className={footerClass}>
            {attemptsLeft > -1 && !hideAttemptsLeft && (
              <p className={attemptsTextClass} aria-live="polite" role="status">
                <RichText
                  value={{
                    ...messages.attemptsLeft,
                    placeholderValues: { attemptsLeft },
                  }}
                />
              </p>
            )}

            {hasMoreHints && (
              <LxButton
                variant="secondary"
                text={intl.formatMessage(
                  showHint ? messages.nextHint : messages.showHint
                )}
                size="large"
                onClickHandler={handleShowHint}
              />
            )}

            {isPrediction &&
              correct !== null &&
              correct !== undefined &&
              onReset && (
                <LxButton
                  variant="secondary"
                  text={intl.formatMessage(messages.resetPrediction)}
                  size="large"
                  onClickHandler={() => {
                    setIsSubmitted(false);
                    dispatch(
                      updateTableState({
                        tableId: id,
                        isSubmitted: false,
                      })
                    );
                    onReset();
                  }}
                />
              )}

            {!(isPrediction && correct !== null && correct !== undefined) && (
              <LxButton
                variant={isPrediction ? "alternative" : "primary"}
                text={submitButtonText}
                size="large"
                onClickHandler={handleSubmit}
                disabled={
                  submitDisabled || hasEmptyEditableCells(dataWithReduxValues)
                }
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* -------------------------------- Exported Component -------------------------------- */

export const TableQuestion = (props: TableQuestionProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <TableQuestionInternal {...props} />
  </TranslationsProvider>
);
